'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface DownloadResourceCardProps {
  resource: {
    title: string;
    description: string;
    fileSize: string;
    fileType: string;
    icon: string;
    fileUrl: string;
  };
}

export default function DownloadResourceCard({ resource }: DownloadResourceCardProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const response = await fetch(resource.fileUrl);
      if (!response.ok) throw new Error('Failed to download file.');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = resource.fileUrl.split('/').pop() || resource.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-institutional p-4 sm:p-6 border border-border hover:shadow-lg transition-shadow duration-300">
      <div className="flex items-start justify-between mb-2 sm:mb-4">
        <div className="flex items-center space-x-1 sm:space-x-2">
          <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
            <Icon
              name={resource.icon as any}
              size={24}
              variant="outline"
              className="text-primary"
            />
          </div>
          <div>
            <h3 className="font-headline text-lg font-semibold text-foreground mb-1">
              {resource.title}
            </h3>
            <div className="flex items-center space-x-2">
              <span className="font-body text-xs text-muted-foreground">
                {resource.fileType}
              </span>
              <span className="text-muted-foreground">•</span>
              <span className="font-body text-xs text-muted-foreground">
                {resource.fileSize}
              </span>
            </div>
          </div>
        </div>
      </div>

      <p className="font-body text-sm text-text-secondary leading-relaxed mb-4">
        {resource.description}
      </p>

      <button
        onClick={handleDownload}
        disabled={isDownloading}
        className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-secondary text-secondary-foreground font-body font-medium text-sm rounded-md hover:bg-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
      >
        <Icon
          name={isDownloading ? 'ArrowPathIcon' : 'ArrowDownTrayIcon'}
          size={20}
          variant="outline"
          className={isDownloading ? 'animate-spin' : ''}
        />
        <span>{isDownloading ? 'Downloading...' : 'Download Document'}</span>
      </button>
    </div>
  );
}